import { createContext, useContext, useMemo, useState } from "react";

const translations = {
  en: {
    nav: {
      home: "Home",
      about: "About",
      projects: "Projects",
      resume: "Resume",
      toggle: "German",
    },
    home: {
      greeting: "Hi There!",
      im: "I'M",
      name: "LOKESH KHANDELWAL",
      subtitle:
        "M.Sc. Web & Data Science student at the University of Koblenz, actively seeking a long-term Working Student Software Engineering role in Germany and planning to build my software engineering career here. I turn ideas into reality through reliable full-stack execution.",
      location: "Koblenz, Germany",
      foundingRole: "Founding Engineer (Pre-Launch Product): MEERAJ SRGL Group",
      availability: "Seeking long-term Working Student Software Engineering roles",
      viewWork: "View My Work",
      contactMe: "Contact Me",
      typewriter: [
        "M.Sc. Web & Data Science",
        "Seeking Long-Term Working Student Roles",
        "Turning Ideas Into Reality",
        "Full-Stack Developer",
        "React | Node.js | Spring Boot",
        "PostgreSQL | Elasticsearch | MongoDB",
        "DSA | System Design | 500+ LeetCode",
      ],
    },
    home2: {
      heading: "WORKING STUDENT SOFTWARE ENGINEERING PROFILE",
      body:
        "I am Lokesh Khandelwal, an M.Sc. Web & Data Science student at the University of Koblenz and a full-stack software engineer seeking long-term Working Student Software Engineering roles in Germany. I plan to stay in Germany long term and build my engineering career here.\n\n" +
        "Alongside my studies, I am Founding Engineer (Pre-Launch Product) for MEERAJ SRGL Group, working on an India-based pre-launch product prototype focused on B2B wholesale and custom bag sourcing workflows, including product discovery, specification comparison, and requirement collection.\n\n" +
        "The project gives me practical experience with product engineering, catalog and inquiry flows, reliability, software-supported workflow design, and turning ideas into reality while keeping software engineering as the main focus of my portfolio.\n\n" +
        "I work across React, Next.js, Node.js, Spring Boot, PostgreSQL, Elasticsearch, MongoDB, Java, C++, JavaScript, and Python. I enjoy building scalable systems, designing clean APIs, connecting data-driven backends with usable interfaces, and turning product requirements into reliable software.\n\n" +
        "I am looking for Working Student roles in full-stack, backend, frontend, platform engineering, or data-driven web applications where I can contribute production experience, product engineering, and disciplined technical execution. I like turning ideas into reality through software that users can actually rely on.",
      findMe: "FIND ME ON",
      connect: "Feel free to connect with me",
    },
    homeShowcase: {
      eyebrow: "Explore my profile",
      heading: "Working-student profile with product engineering",
      subheading:
        "Switch between long-term working-student fit, professional experience, technical strengths, and the tools I use to build reliable, scalable software products.",
      tabs: {
        journey: "Experience",
        skills: "Skills",
        tools: "Tools",
      },
      panels: {
        journey: {
          title: "Professional Journey",
          description:
            "A concise view of my long-term working-student readiness, work experience, education, and selected pre-launch product work.",
        },
        skills: {
          title: "Professional Skillset",
          description:
            "Full-stack, backend, data, DSA, and system design strengths grouped by how I use them in real product development.",
        },
        tools: {
          title: "Tools I Use",
          description:
            "Development, collaboration, API, and delivery tools I use in daily engineering work.",
        },
      },
    },
    contactForm: {
      title: "Contact Lokesh",
      subtitle:
        "Share a long-term working-student role, project, collaboration, or question. This form sends the details directly to my email.",
      name: "Name",
      namePlaceholder: "Your full name",
      email: "Email",
      emailPlaceholder: "you@company.com",
      company: "Company / Organization",
      companyPlaceholder: "Company, university, or project name",
      inquiryType: "Inquiry Type",
      inquiryOptions: [
        { value: "Working Student Opportunity", label: "Working Student Opportunity" },
        { value: "Recruiter / Hiring", label: "Recruiter / Hiring" },
        { value: "MEERAJ SRGL / Prototype Discussion", label: "MEERAJ SRGL / Prototype Discussion" },
        { value: "Project Inquiry", label: "Project Inquiry" },
      ],
      message: "Message",
      messagePlaceholder: "Briefly describe the working-student role, long-term fit, project, timeline, or what you would like to discuss.",
      cancel: "Close",
      submit: "Send Message",
      sending: "Sending...",
      success: "Thanks. Your message has been sent.",
      validationError: "Please add your name, a valid email, and a clear message.",
      sendError: "Something went wrong while sending. Please try again or contact me directly by email.",
    },
    about: {
      heading: "Know Who I'M",
      journeyHeading: "Professional Journey",
      skillHeading: "Professional Skillset",
      toolsHeading: "Tools I use",
    },
    aboutCard: {
      body:
        "I work across full-stack product development: React interfaces, Node.js and Spring Boot services, PostgreSQL and MongoDB data models, Elasticsearch search systems, backend integrations, testing, and delivery workflows.\n\n" +
        "Alongside my studies, I am Founding Engineer (Pre-Launch Product) for MEERAJ SRGL Group, working on an India-based pre-launch product prototype focused on B2B wholesale and custom bag sourcing workflows, including product discovery, specification comparison, and requirement collection.\n\n" +
        "I keep the public details selective: the work gives me experience with product catalog structure, inquiry handling, custom bag design workflows, technology reliability, organized software-supported flows, and turning ideas into reality. I enjoy building maintainable and useful software.\n\n" +
        "At ZAGENO, I contributed to marketplace product features, APIs, search infrastructure, integrations, analytics tracking, and maintainable full-stack workflows.\n\n" +
        "At Nokia, I worked on enterprise workflows, React-based modernization, reporting systems, PDF export, backend integration, and user-focused improvements for cloud network verification tooling.\n\n" +
        "I have solved 500+ LeetCode problems focused on DSA and enjoy scalable system design. I am currently based in Koblenz, Germany, pursuing my M.Sc. in Web & Data Science, seeking a long-term Working Student Software Engineering role, and planning to build my career in Germany.\n\n" +
        "Apart from coding, here are a few activities I enjoy:",
      activities: ["Playing Games", "High Intensity Workouts", "Travelling", "Learning German"],
      quote: "Build software with clarity, care, and purpose.",
    },
    professional: {
      experience: {
        heading: "Experience",
        items: [
          {
            title: "Founding Engineer (Pre-Launch Product)",
            organization: "MEERAJ SRGL Group",
            period: "Jan 2026 - Present",
            location: "India-based / Pre-launch",
            description:
              "Building an India-based pre-launch product prototype focused on B2B wholesale and custom bag sourcing workflows, including product discovery, specification comparison, and requirement collection. The work includes product engineering, catalog and inquiry flows, data modeling, reliability, software-supported workflow design, and turning ideas into reality.",
            skills: [
              "Product Development",
              "Product Engineering",
              "Prototype Development",
              "Technology Reliability",
              "B2B",
              "Wholesale",
              "Next.js",
              "PostgreSQL",
              "Workflow Design",
              "Requirement Collection",
              "Specification Comparison",
              "Catalog Modeling",
            ],
          },
          {
            title: "Software Development Engineer 1",
            organization: "ZAGENO Inc. | Full-time",
            period: "Jun 2024 - Aug 2025",
            location: "Bengaluru, Karnataka, India | Hybrid",
            description:
              "Developed and maintained full-stack marketplace features for a global B2B platform, working across React, TypeScript, backend APIs, search, integrations, and product workflows. Collaborated with product and engineering teams to convert product requirements into scalable, production-ready solutions for professional users.",
            skills: [
              "React",
              "TypeScript",
              "Java",
              "Spring Boot",
              "Node.js",
              "Python",
              "Elasticsearch",
              "Redis",
            ],
          },
          {
            title: "Software Engineer Intern",
            organization: "Nokia | Full-time",
            period: "Aug 2023 - May 2024",
            location: "Bengaluru, Karnataka, India | Hybrid",
            description:
              "Contributed to full-stack development for a Cloud Network Verification system. Built and refined enterprise workflows, reporting features, React-based interfaces, backend-connected flows, and user-focused improvements in a large engineering environment.",
            skills: [
              "React",
              "JavaScript",
              "Java",
              "Spring Boot",
              "Python",
              "MySQL",
              "Material UI",
              "ReactPDF",
            ],
          },
        ],
      },
      education: {
        heading: "Education",
        items: [
          {
            title: "M.Sc. Web and Data Science",
            organization: "University of Koblenz",
            period: "Oct 2025 - Present",
            location: "Koblenz, Germany",
            description:
              "Advanced study focused on modern web systems, data-driven applications, information retrieval, software engineering, and practical research-oriented problem solving.",
            skills: ["Web Engineering", "Data Science", "Information Retrieval", "Research"],
          },
          {
            title: "B.E. Computer Science and Engineering",
            organization: "Visvesvaraya Technological University",
            period: "Jul 2020 - Jul 2024",
            location: "Bengaluru, Karnataka, India",
            description:
              "Built a strong foundation in computer science, software development, algorithms, databases, operating systems, networking, and full-stack project development.",
            skills: ["DSA", "Algorithms", "Databases", "Software Engineering", "Web Development"],
          },
        ],
      },
    },
    projects: {
      heading: "Selected Projects",
      subheading: "A few projects that show full-stack, backend, data, mobile, AI, marketplace, and product execution work.",
      note: "Project reports, implementation details, and deeper technical context are available on request.",
      requestLabel: "Details available on request",
      items: {
        meerajSrgl: {
          title: "MEERAJ SRGL Group",
          description:
            "Building an India-based pre-launch product prototype focused on B2B wholesale and custom bag sourcing workflows, including product discovery, specification comparison, requirement collection, and turning ideas into reality.",
        },
        istoreApp: {
          title: "iStore - Mobile App",
          description:
            "Cross-platform shopping app built with React Native and Expo. It includes authentication, product discovery, cart, checkout, order tracking, and API-driven state management.",
        },
        istoreWeb: {
          title: "iStore - Web Platform",
          description:
            "Full-stack MERN e-commerce platform with REST APIs, authentication, product search, cart and checkout workflows, payments, image uploads, and an admin dashboard.",
        },
        wasteAI: {
          title: "Smart Waste Segregation",
          description:
            "Side AI exploration for waste classification using TensorFlow and Streamlit. Included as practical ML exposure; deeper report available on request.",
        },
        chitChat: {
          title: "chitChat",
          description:
            "Realtime chat application built with React, Material UI, and Firebase. It supports messaging, media sharing, reactions, and live updates.",
        },
        movingCar: {
          title: "Moving Car Simulation",
          description:
            "Interactive browser-based car simulation built with HTML, CSS, and JavaScript to practice animation and frontend fundamentals.",
        },
        blog: {
          title: "iBlog",
          description:
            "Clean blog template built with HTML, CSS, and JavaScript for presenting posts, personal writing, and portfolio content.",
        },
        switch: {
          title: "Switch.io",
          description:
            "Browser extension for quickly toggling dark mode from the toolbar with a simple and lightweight user interaction.",
        },
        courier: {
          title: "Courier Management System",
          description:
            "Academic DBMS project for courier tracking, branch/staff workflows, status updates, complaints, and reporting. Report available on request.",
        },
        pacman: {
          title: "Pacman - OpenGL",
          description:
            "Computer Graphics mini project implementing Pacman-style gameplay with maze movement, pellets, difficulty levels, and OpenGL-based rendering. Report available on request.",
        },
      },
    },
    footer: {
      designed: "Designed and Developed by Lokesh Khandelwal",
      copyright: "Copyright (c) {{year}} LK",
    },
  },

  de: {
    nav: {
      home: "Startseite",
      about: "Über mich",
      projects: "Projekte",
      resume: "CV",
      toggle: "English",
    },
    home: {
      greeting: "Hallo!",
      im: "ICH BIN",
      name: "LOKESH KHANDELWAL",
      subtitle:
        "Ich bin M.Sc.-Student im Bereich Web & Data Science an der Universitaet Koblenz und suche aktiv eine langfristige Werkstudentenstelle im Software Engineering in Deutschland. Ich plane, langfristig in Deutschland zu bleiben und hier meine Engineering-Karriere aufzubauen. Ich setze Ideen durch verlaessliche Full-Stack-Umsetzung in Realitaet um.",
      location: "Koblenz, Deutschland",
      foundingRole: "Founding Engineer (Pre-Launch Product): MEERAJ SRGL Group",
      availability: "Suche langfristige Werkstudentenstellen im Software Engineering",
      viewWork: "Meine Projekte ansehen",
      contactMe: "Kontakt",
      typewriter: [
        "M.Sc. Web & Data Science",
        "Suche langfristige Werkstudentenstellen",
        "Ideen in Realitaet umsetzen",
        "Full-Stack Developer",
        "React | Node.js | Spring Boot",
        "PostgreSQL | Elasticsearch | MongoDB",
        "DSA | System Design | 500+ LeetCode",
      ],
    },
    home2: {
      heading: "WERKSTUDENT SOFTWARE ENGINEERING PROFIL",
      body:
        "Ich bin Lokesh Khandelwal, M.Sc.-Student im Bereich Web & Data Science an der Universitaet Koblenz und Full-Stack Software Engineer auf der Suche nach langfristigen Werkstudentenstellen in Deutschland. Ich plane, langfristig in Deutschland zu bleiben und hier meine Engineering-Karriere aufzubauen.\n\n" +
        "Neben meinem Studium bin ich Founding Engineer (Pre-Launch Product) fuer MEERAJ SRGL Group und arbeite an einem Indien-basierten Pre-Launch-Produktprototyp fuer B2B-Wholesale- und Custom-Bag-Sourcing-Workflows, einschliesslich Product Discovery, Specification Comparison und Requirement Collection.\n\n" +
        "Das Projekt gibt mir praktische Erfahrung mit Product Engineering, Katalog- und Inquiry-Flows, Reliability, softwaregestuetztem Workflow-Design und damit, Ideen in Realitaet umzusetzen, waehrend Software Engineering der Hauptfokus meines Portfolios bleibt.\n\n" +
        "Ich arbeite mit React, Next.js, Node.js, Spring Boot, PostgreSQL, Elasticsearch, MongoDB, Java, C++, JavaScript und Python. Ich entwickle gerne skalierbare Systeme, klare APIs, datengetriebene Backends und nutzbare Interfaces, die Produktanforderungen zuverlaessig loesen.\n\n" +
        "Ich suche Werkstudentenstellen in Full-Stack, Backend, Frontend, Platform Engineering oder datengetriebenen Webanwendungen, in denen ich Produktionserfahrung, Product Engineering und disziplinierte technische Umsetzung einbringen kann. Ich setze gerne Ideen durch Software in Realitaet um, auf die Nutzer sich verlassen koennen.",
      findMe: "FIND MICH AUF",
      connect: "Verbinde dich gerne mit mir",
    },
    homeShowcase: {
      eyebrow: "Profil entdecken",
      heading: "Werkstudentenprofil mit Product Engineering",
      subheading:
        "Wechsle zwischen langfristigem Werkstudenten-Fit, Berufserfahrung, technischen Staerken und den Tools, mit denen ich zuverlaessige und skalierbare Softwareprodukte entwickle.",
      tabs: {
        journey: "Erfahrung",
        skills: "Skills",
        tools: "Tools",
      },
      panels: {
        journey: {
          title: "Beruflicher Weg",
          description:
            "Ein kompakter Ueberblick ueber langfristigen Werkstudenten-Fit, Berufserfahrung, Ausbildung und ausgewaehlte Pre-Launch-Produktarbeit.",
        },
        skills: {
          title: "Professionelle Fähigkeiten",
          description:
            "Full-Stack-, Backend-, Daten-, DSA- und System-Design-Stärken, gruppiert danach, wie ich sie in echter Produktentwicklung einsetze.",
        },
        tools: {
          title: "Werkzeuge, die ich nutze",
          description:
            "Entwicklungs-, Kollaborations-, API- und Delivery-Tools aus meiner täglichen Engineering-Arbeit.",
        },
      },
    },
    contactForm: {
      title: "Lokesh kontaktieren",
      subtitle:
        "Teile eine langfristige Werkstudentenstelle, ein Projekt, eine Zusammenarbeit oder eine Frage. Dieses Formular sendet die Details direkt an meine E-Mail.",
      name: "Name",
      namePlaceholder: "Vollständiger Name",
      email: "E-Mail",
      emailPlaceholder: "du@firma.com",
      company: "Firma / Organisation",
      companyPlaceholder: "Firma, Universität oder Projektname",
      inquiryType: "Anfrageart",
      inquiryOptions: [
        { value: "Working Student Opportunity", label: "Werkstudentenstelle" },
        { value: "Recruiter / Hiring", label: "Recruiting / Einstellung" },
        { value: "MEERAJ SRGL / Prototype Discussion", label: "MEERAJ SRGL / Prototyp" },
        { value: "Project Inquiry", label: "Projektanfrage" },
      ],
      message: "Nachricht",
      messagePlaceholder: "Beschreibe kurz Werkstudentenstelle, langfristigen Fit, Projekt, Zeitplan oder was du besprechen moechtest.",
      cancel: "Schließen",
      submit: "Nachricht senden",
      sending: "Senden...",
      success: "Danke. Deine Nachricht wurde gesendet.",
      validationError: "Bitte Namen, gültige E-Mail und eine klare Nachricht eintragen.",
      sendError: "Beim Senden ist etwas schiefgelaufen. Bitte erneut versuchen oder direkt per E-Mail kontaktieren.",
    },
    about: {
      heading: "Lerne mich kennen",
      journeyHeading: "Beruflicher Weg",
      skillHeading: "Professionelle Fähigkeiten",
      toolsHeading: "Werkzeuge, die ich nutze",
    },
    aboutCard: {
      body:
        "Ich arbeite in der Full-Stack-Produktentwicklung: React Interfaces, Node.js und Spring Boot Services, PostgreSQL und MongoDB Datenmodelle, Elasticsearch Suchsysteme, Backend-Integrationen, Testing und Delivery Workflows.\n\n" +
        "Neben meinem Studium bin ich Founding Engineer (Pre-Launch Product) fuer MEERAJ SRGL Group und arbeite an einem Indien-basierten Pre-Launch-Produktprototyp fuer B2B-Wholesale- und Custom-Bag-Sourcing-Workflows, einschliesslich Product Discovery, Specification Comparison und Requirement Collection.\n\n" +
        "Ich halte die oeffentlichen Details bewusst selektiv: Die Arbeit gibt mir Erfahrung mit Produktkatalog-Struktur, Inquiry Handling, Custom-Bag-Design-Workflows, Technology Reliability, organisierten softwaregestuetzten Ablaeufen und damit, Ideen in Realitaet umzusetzen. Ich baue gerne wartbare und nutzbare Software.\n\n" +
        "Bei ZAGENO habe ich an Marketplace Produktfeatures, APIs, Suchinfrastruktur, Integrationen, Analytics Tracking und wartbaren Full-Stack Workflows mitgearbeitet.\n\n" +
        "Bei Nokia habe ich an Enterprise Workflows, React-basierter Modernisierung, Reporting Systemen, PDF Export, Backend Integration und nutzerorientierten Verbesserungen für Cloud Network Verification Tools gearbeitet.\n\n" +
        "Ich habe 500+ LeetCode-Aufgaben mit Fokus auf DSA geloest und arbeite gerne an skalierbarem System Design. Aktuell lebe ich in Koblenz, studiere M.Sc. Web & Data Science, suche eine langfristige Werkstudentenstelle im Software Engineering und plane, meine Karriere in Deutschland aufzubauen.\n\n" +
        "Neben dem Programmieren mag ich:",
      activities: ["Spiele spielen", "HIIT Workouts", "Reisen", "Deutsch lernen"],
      quote: "Software mit Klarheit, Sorgfalt und Zweck bauen.",
    },
    professional: {
      experience: {
        heading: "Erfahrung",
        items: [
          {
            title: "Founding Engineer (Pre-Launch Product)",
            organization: "MEERAJ SRGL Group",
            period: "Jan 2026 - Heute",
            location: "Indien-basiert / Pre-Launch",
            description:
              "Building an India-based pre-launch product prototype focused on B2B wholesale and custom bag sourcing workflows, including product discovery, specification comparison, and requirement collection. Die Arbeit umfasst Product Engineering, Katalog- und Inquiry-Flows, Datenmodellierung, Reliability, softwaregestuetztes Workflow-Design und turning ideas into reality.",
            skills: [
              "Product Development",
              "Product Engineering",
              "Prototype Development",
              "Technology Reliability",
              "B2B",
              "Wholesale",
              "Next.js",
              "PostgreSQL",
              "Workflow Design",
              "Requirement Collection",
              "Specification Comparison",
              "Catalog Modeling",
            ],
          },
          {
            title: "Software Development Engineer 1",
            organization: "ZAGENO Inc. | Vollzeit",
            period: "Jun 2024 - Aug 2025",
            location: "Bengaluru, Karnataka, Indien | Hybrid",
            description:
              "Entwicklung und Wartung von Full-Stack Marketplace Features für eine globale B2B Plattform über React, TypeScript, Backend APIs, Suche, Integrationen und Produkt-Workflows hinweg. Zusammenarbeit mit Produkt- und Engineering-Teams, um Produktanforderungen in skalierbare, produktionsreife Lösungen zu übersetzen.",
            skills: [
              "React",
              "TypeScript",
              "Java",
              "Spring Boot",
              "Node.js",
              "Python",
              "Elasticsearch",
              "Redis",
            ],
          },
          {
            title: "Software Engineer Intern",
            organization: "Nokia | Vollzeit",
            period: "Aug 2023 - Mai 2024",
            location: "Bengaluru, Karnataka, Indien | Hybrid",
            description:
              "Full-Stack Entwicklung für ein Cloud Network Verification System. Aufbau und Verbesserung von Enterprise Workflows, Reporting Features, React-basierten Oberflächen, backend-verbundenen Abläufen und nutzerorientierten Verbesserungen in einer großen Engineering Umgebung.",
            skills: [
              "React",
              "JavaScript",
              "Java",
              "Spring Boot",
              "Python",
              "MySQL",
              "Material UI",
              "ReactPDF",
            ],
          },
        ],
      },
      education: {
        heading: "Ausbildung",
        items: [
          {
            title: "M.Sc. Web and Data Science",
            organization: "Universität Koblenz",
            period: "Okt 2025 - Heute",
            location: "Koblenz, Deutschland",
            description:
              "Vertiefendes Studium mit Fokus auf moderne Websysteme, datengetriebene Anwendungen, Information Retrieval, Software Engineering und praxisnahe Forschung.",
            skills: ["Web Engineering", "Data Science", "Information Retrieval", "Research"],
          },
          {
            title: "B.E. Computer Science and Engineering",
            organization: "Visvesvaraya Technological University",
            period: "Jul 2020 - Jul 2024",
            location: "Bengaluru, Karnataka, Indien",
            description:
              "Solide Grundlage in Computer Science, Softwareentwicklung, Algorithmen, Datenbanken, Betriebssystemen, Netzwerken und Full-Stack Projektentwicklung.",
            skills: ["DSA", "Algorithms", "Databases", "Software Engineering", "Web Development"],
          },
        ],
      },
    },
    projects: {
      heading: "Ausgewahlte Projekte",
      subheading: "Einige Projekte aus Full-Stack, Backend, Data, Mobile, AI, Marketplace und Produktumsetzung.",
      note: "Projektberichte, Implementierungsdetails und tieferer technischer Kontext sind auf Anfrage verfügbar.",
      requestLabel: "Details auf Anfrage verfügbar",
      items: {
        meerajSrgl: {
          title: "MEERAJ SRGL Group",
          description:
            "Building an India-based pre-launch product prototype focused on B2B wholesale and custom bag sourcing workflows, including product discovery, specification comparison, requirement collection, and turning ideas into reality.",
        },
        istoreApp: {
          title: "iStore - Mobile App",
          description:
            "Cross-platform Shopping App mit React Native und Expo. Enthalt Authentifizierung, Produktsuche, Warenkorb, Checkout, Bestellverfolgung und API-basiertes State Management.",
        },
        istoreWeb: {
          title: "iStore - Web Platform",
          description:
            "Full-Stack MERN E-Commerce Plattform mit REST APIs, Authentifizierung, Produktsuche, Warenkorb- und Checkout-Workflows, Zahlungen, Bild-Uploads und Admin Dashboard.",
        },
        wasteAI: {
          title: "Smart Waste Segregation",
          description:
            "Seitliche AI-Exploration zur Waste Classification mit TensorFlow und Streamlit. Als praktische ML-Erfahrung enthalten; tieferer Bericht auf Anfrage verfügbar.",
        },
        chitChat: {
          title: "chitChat",
          description:
            "Realtime Chat Anwendung mit React, Material UI und Firebase. Unterstützt Messaging, Medienfreigabe, Reaktionen und Live Updates.",
        },
        movingCar: {
          title: "Moving Car Simulation",
          description:
            "Interaktive browserbasierte Autosimulation mit HTML, CSS und JavaScript zum Üben von Animation und Frontend-Grundlagen.",
        },
        blog: {
          title: "iBlog",
          description:
            "Sauberes Blog Template mit HTML, CSS und JavaScript für Posts, persönliche Texte und Portfolio Inhalte.",
        },
        switch: {
          title: "Switch.io",
          description:
            "Browser Erweiterung zum schnellen Umschalten in den Dark Mode direkt über die Toolbar.",
        },
        courier: {
          title: "Courier Management System",
          description:
            "Akademisches DBMS-Projekt für Courier Tracking, Branch-/Staff-Workflows, Statusupdates, Beschwerden und Reporting. Bericht auf Anfrage verfügbar.",
        },
        pacman: {
          title: "Pacman - OpenGL",
          description:
            "Computer-Graphics-Mini-Projekt mit Pacman-Gameplay, Maze Movement, Pellets, Schwierigkeitsstufen und OpenGL Rendering. Bericht auf Anfrage verfügbar.",
        },
      },
    },
    footer: {
      designed: "Entworfen und entwickelt von Lokesh Khandelwal",
      copyright: "Copyright (c) {{year}} LK",
    },
  },
};

const TranslationContext = createContext({
  language: "en",
  toggleLanguage: () => {},
  t: (key) => key,
});

function getNestedValue(obj, path) {
  return path.split(".").reduce((acc, part) => {
    if (acc && Object.prototype.hasOwnProperty.call(acc, part)) {
      return acc[part];
    }
    return undefined;
  }, obj);
}

export function TranslationProvider({ children }) {
  const [language, setLanguage] = useState("en");

  const toggleLanguage = () => {
    setLanguage((prev) => (prev === "en" ? "de" : "en"));
  };

  const value = useMemo(() => {
    const t = (key, fallback) => {
      const result = getNestedValue(translations[language], key);
      return result !== undefined ? result : fallback ?? key;
    };
    return { language, toggleLanguage, t };
  }, [language]);

  return (
    <TranslationContext.Provider value={value}>
      {children}
    </TranslationContext.Provider>
  );
}

export function useTranslation() {
  return useContext(TranslationContext);
}
